import { FC, ReactNode, useMemo, useRef, useState } from 'react';
import { Button, Col, Input, InputRef, Row } from 'antd';
import { matchSorter } from 'match-sorter';
import { TbArrowRight, TbCircleXFilled, TbSearch, TbSettingsSearch, TbSlash } from 'react-icons/tb';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useSlashSearchFocus } from '@/hooks/useSlashSearchFocus';
import EmptySection from '@/shared/components/layouts/views/EmptySection';

import {
    SettingsSearchEntry,
    SettingsSearchPage,
    settingsSearchPages,
    settingsSearchSections,
} from '../../settingsSearchRegistry';

type SettingsSearchMatch = SettingsSearchEntry & {
    breadcrumb: string;
    pageKey: string;
    searchableText: string;
};

const normalizeSearchTerm = (value: string) => value.trim().toLowerCase();

const getAllDescendants = (
    entry: SettingsSearchEntry,
    page: SettingsSearchPage,
    parentLabels: string[] = [],
): SettingsSearchMatch[] => {
    const labels = [...parentLabels, entry.label];
    const breadcrumb = labels.join(' › ');
    const searchableText = [entry.label, entry.description, ...(entry.aliases ?? [])].filter(Boolean).join(' ');

    return [
        {
            ...entry,
            breadcrumb,
            pageKey: page.key,
            searchableText,
        },
        ...(entry.children ?? []).flatMap((child) => getAllDescendants(child, page, labels)),
    ];
};

const settingsSearchMatches = settingsSearchPages.flatMap((page) =>
    (page.children ?? []).flatMap((child) => getAllDescendants(child, page, [page.label])),
);

const getPageSearchText = (page: SettingsSearchPage) =>
    [page.label, page.description, ...(page.aliases ?? [])].filter(Boolean).join(' ');

const getSearchResultRouteState = (entry: Pick<SettingsSearchEntry, 'label' | 'type'>) => ({
    settingsSearchRequestId: Date.now(),
    settingsSearchTarget: entry.label,
    settingsSearchTargetType: entry.type,
});

const getEntryTypeLabel = (type: SettingsSearchEntry['type']) => {
    switch (type) {
        case 'tab':
            return 'Tab';
        case 'section':
            return 'Section';
        case 'field':
            return 'Setting';
        case 'page':
        default:
            return 'Page';
    }
};

const renderIconLabel = (icon: ReactNode, label: string, color: string) => (
    <Row align='middle' gutter={[0, 0]} wrap={false}>
        {icon && (
            <Col flex='none' style={{ color }}>
                {icon}
            </Col>
        )}
        <Col flex='auto' style={{ minWidth: 0 }}>
            <span>{label}</span>
        </Col>
    </Row>
);

const Sider: FC = (): JSX.Element => {
    const location = useLocation();
    const navigate = useNavigate();
    const { token } = useAntDesignTheme();

    const [searchTerm, setSearchTerm] = useState('');
    const searchInputRef = useRef<InputRef>(null);

    useSlashSearchFocus(searchInputRef, true, { priority: -1 });

    const normalizedSearchTerm = normalizeSearchTerm(searchTerm);
    const isSearching = normalizedSearchTerm.length > 0;

    const pageMatches = useMemo(() => {
        if (!isSearching) {
            return new Set<string>();
        }

        return new Set(
            matchSorter(settingsSearchPages, normalizedSearchTerm, {
                keys: [getPageSearchText],
                threshold: matchSorter.rankings.CONTAINS,
            }).map((page) => page.key),
        );
    }, [isSearching, normalizedSearchTerm]);

    const nestedMatchesByPage = useMemo(() => {
        if (!isSearching) {
            return new Map<string, SettingsSearchMatch[]>();
        }

        const matches = matchSorter(settingsSearchMatches, normalizedSearchTerm, {
            keys: ['searchableText'],
            threshold: matchSorter.rankings.CONTAINS,
        });

        return matches.reduce((acc, match) => {
            const pageMatches = acc.get(match.pageKey) ?? [];

            acc.set(match.pageKey, [...pageMatches, match]);

            return acc;
        }, new Map<string, SettingsSearchMatch[]>());
    }, [isSearching, normalizedSearchTerm]);

    const visiblePages = useMemo(() => {
        if (!isSearching) {
            return settingsSearchPages;
        }

        return settingsSearchPages.filter((page) => pageMatches.has(page.key) || nestedMatchesByPage.has(page.key));
    }, [isSearching, nestedMatchesByPage, pageMatches]);

    const isRouteActive = (route: string) => {
        return location.pathname === route || location.pathname.startsWith(`${route}/`);
    };

    const handleNavigate = (entry: SettingsSearchEntry) => {
        navigate(entry.route, {
            state: isSearching ? getSearchResultRouteState(entry) : undefined,
        });
    };

    return (
        <div style={{ marginLeft: '1rem' }}>
            <h4 style={{ marginTop: '0.75rem', marginBottom: '1rem', color: token.colorTextTertiary, fontWeight: 400 }}>
                Settings
            </h4>
            <Input
                ref={searchInputRef}
                prefix={
                    <TbSettingsSearch
                        size='1rem'
                        style={{ marginRight: '0.25rem', color: token.colorTextQuaternary }}
                    />
                }
                placeholder='Search Settings...'
                suffix={<TbSlash size='1rem' style={{ color: token.colorTextQuaternary }} />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                    marginBottom: '0.5rem',
                    paddingLeft: '0.5rem',
                    paddingRight: '0.5rem',
                }}
                allowClear={{
                    clearIcon: (
                        <TbCircleXFilled
                            size='1rem'
                            style={{ verticalAlign: '-0.2rem', color: token.colorTextSecondary }}
                        />
                    ),
                }}
            />

            {settingsSearchSections.map((section) => {
                const pages = visiblePages.filter((page) => page.sectionKey === section.key);

                if (!pages.length) {
                    return null;
                }

                return (
                    <section key={section.key}>
                        <div style={{ marginBottom: '0.75rem', marginLeft: '0.5rem' }}>
                            <small style={{ color: token.colorTextTertiary }}>{section.label}</small>
                        </div>

                        {pages.map((page) => {
                            const pageNestedMatches = nestedMatchesByPage.get(page.key) ?? [];
                            const active = isRouteActive(page.route);

                            return (
                                <div key={page.key} style={{ marginBottom: isSearching ? '0.75rem' : undefined }}>
                                    <Button
                                        type='text'
                                        block
                                        style={{
                                            marginBottom:
                                                isSearching && pageNestedMatches.length ? '0.25rem' : '0.5rem',
                                            textAlign: 'left',
                                            backgroundColor: active ? token.colorBgBase : undefined,
                                            justifyContent: 'flex-start',
                                        }}
                                        onClick={() => handleNavigate(page)}
                                    >
                                        <span style={{ color: token.colorTextSecondary, width: '100%' }}>
                                            {renderIconLabel(page.icon, page.label, token.colorTextTertiary)}
                                        </span>
                                    </Button>

                                    {isSearching && pageNestedMatches.length > 0 && (
                                        <div style={{ marginLeft: '1.375rem' }}>
                                            {pageNestedMatches.map((match) => (
                                                <Button
                                                    key={match.key}
                                                    type='text'
                                                    block
                                                    size='small'
                                                    style={{
                                                        height: 'auto',
                                                        marginBottom: '0.25rem',
                                                        textAlign: 'left',
                                                        justifyContent: 'flex-start',
                                                    }}
                                                    onClick={() => handleNavigate(match)}
                                                >
                                                    <Row
                                                        align='middle'
                                                        gutter={[6, 0]}
                                                        wrap={false}
                                                        style={{ width: '100%' }}
                                                    >
                                                        <Col flex='auto' style={{ minWidth: 0 }}>
                                                            <div
                                                                style={{
                                                                    color: token.colorTextSecondary,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                }}
                                                            >
                                                                {match.label}
                                                            </div>
                                                            <small
                                                                style={{
                                                                    color: token.colorTextTertiary,
                                                                    display: 'block',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                }}
                                                            >
                                                                {getEntryTypeLabel(match.type)} · {match.breadcrumb}
                                                            </small>
                                                        </Col>
                                                        <Col flex='none'>
                                                            <TbArrowRight
                                                                size='0.875rem'
                                                                style={{
                                                                    color: token.colorTextQuaternary,
                                                                    verticalAlign: '-0.125rem',
                                                                }}
                                                            />
                                                        </Col>
                                                    </Row>
                                                </Button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </section>
                );
            })}

            {isSearching && visiblePages.length < 1 && (
                <EmptySection
                    height={16 * 12}
                    icon={<TbSearch size='1rem' style={{ color: token.colorTextTertiary }} />}
                    label='No Settings Found'
                    description='Refine your search to find a settings page, tab, or setting.'
                />
            )}
        </div>
    );
};

export default Sider;
